import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom';

import './App.css';
import Register from './Register';
import LoginPage from './LoginPage';
import ResetPasswordPage from './ResetPasswordPage';
import Dashboard from './Dashboard';
import OrganisationMembershipWorkspace from './OrganisationMembershipWorkspace';
import OwnerSetupPage from './OwnerSetupPage';
import JoinOrganisationPage from './JoinOrganisationPage';
import MembersPage from './MembersPage';
import BookAircraft from './BookAircraft';
import ProtectedRoute from './ProtectedRoute';
import PlatformAdminDashboard from './PlatformAdminDashboard';
import AdminPage from './AdminPage';
import MyBookings from './MyBookings';
import PilotLogbookPage from './PilotLogbookPage';
import ViewAircraft from './ViewAircraft';
import AirportLookup from './AirportLookup';
import ProfilePage from './ProfilePage';
import DocumentsPage from './DocumentsPage';
import PreFlightPage from './PreFlightPage';
import PostFlightPage from './PostFlightPage';
import AircraftTechLogPage from './AircraftTechLogPage';
import TechLogReviewPage from './TechLogReviewPage';
import ContactPage from './ContactPage';
import UpgradePage from './UpgradePage';
import { useSession } from './SessionContext';
import defaultWingitLogo from './assets/wingitlog-fixed.png';
import {
  shouldHideAppLogo,
  shouldUseCompactAppLogo,
} from './appBranding';

function AppLogo() {
  const location = useLocation();

  const hideLogo = shouldHideAppLogo(location.pathname);
  const compactLogo = shouldUseCompactAppLogo(
    location.pathname,
    location.search
  );

  if (hideLogo) {
    return null;
  }

  return (
    <Link
      to="/contact"
      className={`company-logo-link${compactLogo ? ' company-logo-link-compact' : ''}`}
      aria-label="Open contact page"
    >
      <div className={`company-logo-badge${compactLogo ? ' company-logo-badge-compact' : ''}`}>
        <div className="company-logo-stack">
          <img
            className={`company-logo-image${compactLogo ? ' company-logo-image-compact' : ''}`}
            src={defaultWingitLogo}
            alt="Wingit logo"
          />
        </div>
      </div>
    </Link>
  );
}

function App() {
  const session = useSession();
  const isResetPasswordPath =
    window.location.pathname === '/reset-password';
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash
  );
  const isPasswordRecoveryFlow =
    urlParams.get('type') === 'recovery' ||
    hashParams.get('type') === 'recovery';

  return (
    <Router>
      <div className="App">
        <header className="App-header">
          <AppLogo />

          <div className="app-route-content">
            <Routes>
              <Route
                path="/"
                element={
                  session === undefined ? (
                    <div
                      style={{
                        color: '#b6eaff',
                        padding: '2rem',
                      }}
                    >
                      Restoring session...
                    </div>
                  ) : (
                    <Navigate
                      to={
                        session
                          ? isResetPasswordPath ||
                            isPasswordRecoveryFlow
                            ? '/reset-password'
                            : '/dashboard'
                          : '/register'
                      }
                      replace
                    />
                  )
                }
              />

              <Route
                path="/register-owner"
                element={<OwnerSetupPage />}
              />

              <Route
                path="/register"
                element={<Register />}
              />

              <Route
                path="/join-organisation"
                element={<JoinOrganisationPage />}
              />

              <Route
                path="/login"
                element={<LoginPage />}
              />

              <Route
                path="/reset-password"
                element={<ResetPasswordPage />}
              />

              <Route
                path="/members"
                element={
                  <ProtectedRoute requiredFeature="dashboard">
                    <MembersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredFeature="dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/organisation-memberships"
                element={
                  <ProtectedRoute requiredFeature="dashboard">
                    <OrganisationMembershipWorkspace />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredFeature="admin">
                    <AdminPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/platform-admin"
                element={
                  <ProtectedRoute requiredFeature="admin">
                    <PlatformAdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/book-aircraft"
                element={
                  <ProtectedRoute requiredFeature="book-aircraft">
                    <BookAircraft />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-bookings"
                element={
                  <ProtectedRoute requiredFeature="my-bookings">
                    <MyBookings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pilot-logbook"
                element={
                  <ProtectedRoute requiredFeature="pilot-logbook">
                    <PilotLogbookPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/view-aircraft"
                element={
                  <ProtectedRoute requiredFeature="view-aircraft">
                    <ViewAircraft />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/documents"
                element={
                  <ProtectedRoute requiredFeature="documents">
                    <DocumentsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pre-flight"
                element={
                  <ProtectedRoute requiredFeature="pre-flight">
                    <PreFlightPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/post-flight"
                element={
                  <ProtectedRoute requiredFeature="pre-flight">
                    <PostFlightPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircraft-tech-log"
                element={
                  <ProtectedRoute requiredFeature="aircraft-tech-log">
                    <AircraftTechLogPage />
                  </ProtectedRoute>
                }
              />
<Route
  path="/tech-log-review"
  element={
    <ProtectedRoute requiredFeature="aircraft-tech-log">
      <TechLogReviewPage />
    </ProtectedRoute>
  }
/>
              <Route
                path="/profile"
                element={
                  <ProtectedRoute requiredFeature="profile">
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/upgrade"
                element={
                  <ProtectedRoute requiredFeature="dashboard">
                    <UpgradePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/airport-lookup"
                element={<AirportLookup />}
              />

              <Route
                path="/contact"
                element={<ContactPage />}
              />

              <Route
                path="*"
                element={
                  <div>
                    <div
                      style={{
                        maxWidth: 900,
                        margin: '0 auto',
                        marginBottom: '2.5rem',
                        textAlign: 'center',
                        color: '#eaf6fb',
                        fontSize: '1.2rem',
                        lineHeight: 1.6,
                      }}
                    >
                      <h2
                        style={{
                          color: '#1ecfff',
                          fontWeight: 700,
                          fontSize: '2rem',
                          marginBottom: '0.7rem',
                        }}
                      >
                        Welcome to Wingit – The Free
                        Online Aircraft Booking System
                      </h2>

                      <p>
                        We’re pilots and aviation
                        enthusiasts, just like you.
                        That’s why we built Wingit: a
                        simple, secure, and free booking
                        platform for Flying Schools,
                        Clubs, Groups, Syndicates, and
                        Owners.
                      </p>

                      <p>
                        Whether you fly aeroplanes,
                        gliders, microlights, or balloons,
                        Wingit helps you manage your
                        aircraft, schedule flights, and
                        connect with your aviation
                        community—all in one place.
                      </p>

                      <ul
                        style={{
                          textAlign: 'left',
                          maxWidth: 600,
                          margin: '1.2rem auto',
                          color: '#b6eaff',
                          fontSize: '1.1rem',
                        }}
                      >
                        <li>
                          Create and manage your group or
                          school in minutes
                        </li>

                        <li>
                          Book and share aircraft with
                          ease
                        </li>

                        <li>
                          Add members, instructors, and
                          aircraft with just a few clicks
                        </li>

                        <li>
                          Designed by pilots, for pilots
                          and aviation groups
                        </li>

                        <li>
                          Always free, always secure,
                          always here to help
                        </li>
                      </ul>

                      <p style={{ marginTop: '1.2rem' }}>
                        Ready to get started?{' '}
                        <Link
                          to="/register"
                          style={{
                            color: '#1ecfff',
                            textDecoration: 'underline',
                          }}
                        >
                          Create your account
                        </Link>{' '}
                        or register your group or school
                        today.
                        <br />
                        If you need a hand, just reach
                        out—we’re here for you.
                      </p>
                    </div>
                  </div>
                }
              />
            </Routes>
          </div>
        </header>
      </div>
    </Router>
  );
}

export default App;